import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { DEFAULT_FRAME_STYLES, type FrameStyleOption } from './questionnaireFrameStylesData';
import { DEFAULT_JOINERY_STYLES, type JoineryStyleOption } from './questionnaireJoineryStylesData';
import {
  DEFAULT_TIMBER_SPECIES,
  type QuestionnaireTimberSpecies,
} from './questionnaireTimberSpeciesData';

export interface QuestionnairePublicContent {
  frameStyles: FrameStyleOption[];
  joineryStyles: JoineryStyleOption[];
  timberSpecies: QuestionnaireTimberSpecies[];
}

type StyleRow = {
  id: string;
  title: string;
  description: string;
  image?: string;
  imageUrl?: string;
  order?: number;
};

function mapStyleRows(rows: StyleRow[]): FrameStyleOption[] {
  return rows
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => ({
      id: s.id,
      title: s.title,
      description: s.description,
      image: s.imageUrl || s.image || '',
    }))
    .filter((s) => s.image);
}

/** Shared read-only content for public style guide + questionnaire defaults (anonymous auth). */
export async function fetchQuestionnairePublicContent(): Promise<QuestionnairePublicContent> {
  let frameStyles = DEFAULT_FRAME_STYLES;
  let joineryStyles = DEFAULT_JOINERY_STYLES;
  let timberSpecies = DEFAULT_TIMBER_SPECIES;

  try {
    const snap = await getDoc(doc(db, 'appSettings', 'questionnaire'));
    if (!snap.exists()) {
      return { frameStyles, joineryStyles, timberSpecies };
    }
    const data = snap.data();
    if (data.frameStyles?.length) {
      frameStyles = mapStyleRows(data.frameStyles as StyleRow[]);
    }
    if (data.joineryStyles?.length) {
      joineryStyles = mapStyleRows(data.joineryStyles as StyleRow[]) as JoineryStyleOption[];
    }
    if (data.speciesImages) {
      const imgMap = data.speciesImages as Record<string, string>;
      timberSpecies = timberSpecies.map((sp) =>
        imgMap[sp.id] ? { ...sp, imageUrl: imgMap[sp.id] } : sp
      );
    }
  } catch {
    // defaults
  }

  return { frameStyles, joineryStyles, timberSpecies };
}
